import { Modules } from './Module';
import { DEFAULT_OPERATION_IDS } from '../../../infrastructure/auth/permission-keys';

export interface Operation {
    id: string;
    moduleId: string;
    name: string;
}

type OperationDefinition = Pick<Operation, 'id' | 'name'>;

const DEFAULT_OPERATIONS: OperationDefinition[] = DEFAULT_OPERATION_IDS.map(id => ({
    id,
    name: id.charAt(0).toUpperCase() + id.slice(1)
}));

// Extra operations a module needs on top of the defaults; each one gets its own checkbox in role details.
// e.g. operations: [{ id: 'terminate', name: 'Terminate' }, { id: 'export', name: 'Export' }]
// Ids must be lowercase letters, digits or underscores (no "-", which separates module and operation in a key).
// Keep in sync with PermissionKeys.CUSTOM_OPERATIONS in the API, which rejects ids it does not know.
export const CUSTOM_OPERATIONS: Record<string, OperationDefinition[]> = {};

const OPERATION_ID = /^[a-z0-9_]+$/;

Object.entries(CUSTOM_OPERATIONS).forEach(([moduleId, operations]) => {
    if (!Modules.some(module => module.id === moduleId)) {
        throw new Error(`CUSTOM_OPERATIONS has unknown module: ${moduleId}`);
    }
    const invalid = operations.find(operation => !OPERATION_ID.test(operation.id));
    if (invalid) {
        throw new Error(`Invalid operation id '${invalid.id}' for module ${moduleId}: use lowercase letters, digits or underscores`);
    }
});

export const operationsFor = (moduleId: string): Operation[] =>
    [...DEFAULT_OPERATIONS, ...(CUSTOM_OPERATIONS[moduleId] ?? [])]
        .filter((operation, index, all) => all.findIndex(other => other.id === operation.id) === index)
        .map(operation => ({ ...operation, moduleId }));

export const Operations: Operation[] = Modules.flatMap(module => operationsFor(module.id));
